import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StreakService } from '../../services/StreakService';
import { XpService } from '../../services/XpService';
import { SoundEffectService } from '../../services/SoundEffectService';
import { LearningProgressService } from '../../services/LearningProgressService';
import { ExerciseScoreService } from '../../services/ExerciseScoreService';
import { ChunkyButton, Confetti, GameProgressBar, ShakeWidget, StarRow, FloatingXpBadge } from '../../components/GameUI';

// Listen-and-choose mini-game — plays a target-language phrase via TTS, then
// asks the learner to pick which of 4 options was spoken. Port of the mobile
// app's lib/games/listening_challenge/listening_challenge_screen.dart. Unlike
// most of the other ported games, revealing an answer here does NOT
// auto-advance — the learner taps "Next"/"See Results", matching the Dart
// source exactly.

const ROUNDS_PER_SESSION = 6;

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function pickRounds(pool) {
  return shuffle(pool).slice(0, Math.min(ROUNDS_PER_SESSION, pool.length));
}

function buildOptions(pool, answer) {
  if (!answer) return [];
  const distractors = shuffle(pool.filter((item) => item.id !== answer.id)).slice(0, 3);
  return shuffle([answer, ...distractors]);
}

function speak(text, lang) {
  try {
    if (!text || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    if (lang) utterance.lang = lang;
    window.speechSynthesis.speak(utterance);
  } catch {
    // speech synthesis is a nice-to-have, never block the game on it
  }
}

function AnswerButton({ text, dir, revealed, isAnswer, isPicked, onClick }) {
  let bg = '#FFFFFF';
  let border = '#E2E8F0';
  let fg = '#0F172A';
  if (revealed) {
    if (isAnswer) {
      bg = '#D7FFB8'; border = '#58CC02'; fg = '#2B6B00';
    } else if (isPicked) {
      bg = '#FFDFE0'; border = '#FF4B4B'; fg = '#8B0000';
    } else {
      bg = '#FFFFFF'; border = '#F1F5F9'; fg = '#CBD5E1';
    }
  }
  return (
    <button
      type="button"
      dir={dir}
      disabled={revealed}
      onClick={onClick}
      style={{ background: bg, borderColor: border, color: fg }}
      className="flex w-full items-center justify-between gap-3 rounded-2xl border-[2.5px] px-4 py-3.5 text-left font-extrabold transition-colors duration-150"
    >
      <span className="min-w-0 flex-1 truncate">{text}</span>
      {revealed && (isAnswer || isPicked) && (
        <span className="shrink-0 text-lg" style={{ color: isAnswer ? '#58CC02' : '#FF4B4B' }}>
          {isAnswer ? '✓' : '✕'}
        </span>
      )}
    </button>
  );
}

export default function ListeningChallengeGame() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const {
    pool = [],
    targetLang = { code: '', label: '', dir: 'ltr', tts: '' },
    langPair = 'guest',
  } = state || {};

  const [rounds, setRounds] = useState(() => pickRounds(pool));
  const [roundIndex, setRoundIndex] = useState(0);
  const [options, setOptions] = useState(() => buildOptions(pool, rounds[0]));
  const [picked, setPicked] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);

  const rewardedRef = useRef(false);
  const answer = rounds[roundIndex] || null;

  // Kick off the session once (and only once — pool comes from route state
  // and never changes during this screen's lifetime): guard against missing
  // state, then speak the first round's answer.
  useEffect(() => {
    if (!pool.length) {
      navigate('/games', { replace: true });
      return undefined;
    }
    speak(rounds[0]?.text, targetLang.tts);
    return () => {
      try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const percent = rounds.length ? Math.round((score / rounds.length) * 100) : 0;
  const stars = ExerciseScoreService.starsForPercent(percent);
  const xpEarned = score * 4 + (stars === 3 ? 10 : 0);

  useEffect(() => {
    if (!done || rewardedRef.current) return;
    rewardedRef.current = true;
    StreakService.recordActivity();
    XpService.addXp(xpEarned);
    if (stars === 3) SoundEffectService.playCelebration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  function pick(option) {
    if (revealed || !answer) return;
    const correct = option.id === answer.id;
    setPicked(option.id);
    setRevealed(true);
    if (correct) {
      setScore((s) => s + 1);
      SoundEffectService.playCorrect();
      if (navigator.vibrate) navigator.vibrate(15);
      LearningProgressService.record(langPair, 'listen', 1, { activityId: `listen:${answer.id}` });
    } else {
      SoundEffectService.playWrong();
      if (navigator.vibrate) navigator.vibrate(35);
    }
  }

  function next() {
    if (roundIndex + 1 >= rounds.length) {
      setDone(true);
      return;
    }
    const nextIndex = roundIndex + 1;
    setRoundIndex(nextIndex);
    setOptions(buildOptions(pool, rounds[nextIndex]));
    setPicked(null);
    setRevealed(false);
    speak(rounds[nextIndex]?.text, targetLang.tts);
  }

  function restart() {
    const freshRounds = pickRounds(pool);
    rewardedRef.current = false;
    setRounds(freshRounds);
    setRoundIndex(0);
    setOptions(buildOptions(pool, freshRounds[0]));
    setPicked(null);
    setRevealed(false);
    setScore(0);
    setDone(false);
    speak(freshRounds[0]?.text, targetLang.tts);
  }

  if (!pool.length) return null;

  const perfect = stars === 3;
  const isLastRound = roundIndex + 1 >= rounds.length;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-lg px-4 py-4 sm:px-6">
        <div className="mb-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-500 shadow-sm hover:bg-slate-50">
            ← {t('games.title')}
          </button>
          <h1 className="text-sm font-black text-slate-900">
            {t('games.listeningChallenge', 'Listening Challenge')}
          </h1>
          <span className="w-16" />
        </div>

        {done ? (
          <div className="relative flex flex-col items-center py-10 text-center">
            {perfect && <Confetti />}
            <span className="text-6xl">{perfect ? '🏆' : '🎉'}</span>
            <h2 className="mt-3 text-2xl font-black text-slate-900">
              {t('games.listeningChallengeComplete', 'Listening Challenge complete!')}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {t('games.listeningChallengeScoreLine', '{{score}}/{{total}} correct', {
                score,
                total: rounds.length,
              })}
            </p>
            <div className="mt-3">
              <StarRow count={stars} />
            </div>
            <div className="mt-3">
              <FloatingXpBadge xp={xpEarned} />
            </div>
            <div className="mt-8 w-full max-w-xs">
              <ChunkyButton color="#4F46E5" onClick={restart} className="w-full py-4 text-center text-white">
                {t('games.tryAgain')}
              </ChunkyButton>
              <button onClick={() => navigate('/games')} className="mt-3 w-full rounded-2xl border border-slate-200 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50">
                {t('games.done')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-base font-black text-slate-900">
                {roundIndex + 1}/{rounds.length}
              </span>
              <span className="text-xs font-extrabold text-slate-500">
                {t('games.score')} {score}
              </span>
            </div>
            <div className="mb-7">
              <GameProgressBar value={rounds.length ? roundIndex / rounds.length : 0} color="#4F46E5" />
            </div>

            <div className="mb-7 flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={() => speak(answer?.text, targetLang.tts)}
                aria-label={t('games.listeningChallengeTapToReplay', 'Tap to replay')}
                className="grid h-24 w-24 place-items-center rounded-full text-white transition hover:scale-105 active:scale-95"
                style={{
                  background: 'linear-gradient(135deg, #6366F1, #4F46E5)',
                  boxShadow: '0 10px 30px rgba(79,70,229,0.35)',
                }}
              >
                <span className="text-4xl" aria-hidden="true">🔊</span>
              </button>
              <span className="text-xs text-slate-400">
                {t('games.listeningChallengeTapToReplay', 'Tap to replay')}
              </span>
            </div>

            <div dir={targetLang.dir} className="flex flex-col gap-3">
              {options.map((option) => {
                const isAnswer = option.id === answer?.id;
                const isPicked = option.id === picked;
                return (
                  <ShakeWidget key={option.id} trigger={revealed && isPicked && !isAnswer}>
                    <AnswerButton
                      text={option.text}
                      dir={targetLang.dir}
                      revealed={revealed}
                      isAnswer={isAnswer}
                      isPicked={isPicked}
                      onClick={() => pick(option)}
                    />
                  </ShakeWidget>
                );
              })}
            </div>

            {revealed && (
              <div className="mt-6">
                <ChunkyButton color="#4F46E5" onClick={next} className="w-full py-4 text-center text-white">
                  {isLastRound ? t('games.seeResults', 'See Results →') : t('games.next', 'Next →')}
                </ChunkyButton>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
